import type { RangeValue, FilterValue } from '@/contexts/FiltersContext.context';
import type { Plan } from '@/services/frandroid-api';

export type FilterPredicate = (item: Plan, value: string | string[] | RangeValue) => boolean;

export interface FilterPredicateConfig {
  [filterName: string]: FilterPredicate;
}

const networkPredicate: FilterPredicate = (plan, values) => {
  const networkValues = Array.isArray(values) ? values : [values as string];
  const slug = plan.operator?.networks?.[0]?.slug;
  return slug !== undefined && networkValues.includes(slug);
};

const operatorPredicate: FilterPredicate = (plan, values) => {
  const operatorValues = Array.isArray(values) ? values : [values as string];
  const slug = plan.operator?.slug;
  return operatorValues.includes(slug);
};

export function applyFilters(
  items: Plan[],
  filters: Record<string, FilterValue>,
  predicates: FilterPredicateConfig
): Plan[] {
  return items.filter(item => {
    for (const [filterName, predicate] of Object.entries(predicates)) {
      const value = filters[filterName];
      if (value === null || value === undefined) continue;
      if (Array.isArray(value) && value.length === 0) continue;
      if (!predicate(item, value)) return false;
    }
    return true;
  });
}

function matchesRange(actual: number, value: string | string[] | RangeValue): boolean {
  const range = value as RangeValue;
  if (range.min != null && actual < range.min) return false;
  if (range.max != null && actual > range.max) return false;
  return true;
}

function createRangePredicate(extract: (plan: Plan) => number): FilterPredicate {
  return (plan, value) => matchesRange(extract(plan), value);
}

// Source of truth: MobileAdvancedOptionsRegistry::OPTIONS (theme PHP),
// exposed via window.__APP_DATA__.mobileFilterOptions.advanced.
// Semantics aligned with LemonFrandroidApiProvider: truthy check on
// `plan.extra[extraKey]` (equivalent to PHP's `!empty(...)`).
function mobileAdvancedRegistry(): { value: string; extraKey?: string; planType?: string }[] {
  if (typeof window !== 'undefined') {
    const server = window.__APP_DATA__?.mobileFilterOptions?.advanced;
    if (server && server.length > 0) {
      return server;
    }
  }
  // Fallback dev/SSR — keep aligned with MobileAdvancedOptionsRegistry::OPTIONS.
  return [
    { value: 'bloquable', extraKey: 'is_blocked_plan' },
    { value: '5gCompatible', planType: '5g-plan' },
    { value: '5g-plus', extraKey: '5g-plus' },
    { value: 'multisim', extraKey: 'multisim' },
    { value: 'esim', extraKey: 'esim' },
    { value: 'internationalData', extraKey: 'data_international' },
  ];
}

function matchesMobileAdvancedOption(plan: Plan, value: string): boolean {
  const entry = mobileAdvancedRegistry().find(opt => opt.value === value);
  if (!entry) return true;
  if (entry.extraKey) {
    return Boolean(plan.extra?.[entry.extraKey]);
  }
  if (entry.planType) {
    return plan.type === entry.planType;
  }
  return true;
}

export const mobilePredicates: FilterPredicateConfig = {
  'internet-mobile': (plan, value) => {
    const data = plan.extra.data.data;
    switch (value) {
      case 'less-10': return data < 10;
      case '20-and-more': return data >= 20;
      case '100-and-more': return data >= 100;
      case '300-and-more': return data >= 300;
      default: return true;
    }
  },

  'budget-mobile': (plan, value) => {
    const price = plan.offer.price.price;
    switch (value) {
      case 'less-5': return price < 5;
      case 'less-10': return price < 10;
      case 'less-15': return price < 15;
      case 'more-15': return price > 15;
      default: return true;
    }
  },

  'budget-mobile-range': createRangePredicate(p => p.offer.price.price),
  'internet-mobile-range': createRangePredicate(p => p.extra.data.data),

  'network-mobile': networkPredicate,

  'operator-mobile': operatorPredicate,

  'options-mobile': (plan, values) => {
    const optionValues = Array.isArray(values) ? values : [values as string];
    return optionValues.some(option => matchesMobileAdvancedOption(plan, option));
  },
};

export const internetPredicates: FilterPredicateConfig = {
  'technology-internet': (plan, values) => {
    const techValues = Array.isArray(values) ? values : [values as string];
    const planTechnologies: string[] = (plan.extra?.internet_type ?? []).map(t => (typeof t === 'string' ? t : t?.value));
    return techValues.some(tech => {
      switch (tech) {
        case 'fibre': return planTechnologies.includes('fibre');
        case 'adsl': return planTechnologies.includes('adsl');
        case 'box-5g': return planTechnologies.includes('5g');
        default: return false;
      }
    });
  },

  'budget-internet': (plan, value) => {
    const price = plan.offer.price.price;
    switch (value) {
      case 'less-20': return price < 20;
      case 'between-20-30': return price >= 20 && price < 30;
      case 'more-30': return price > 30;
      default: return true;
    }
  },

  'budget-internet-range': createRangePredicate(p => p.offer.price.price),

  'speed-internet': (plan, value) => {
    const planSpeed = plan.extra?.internet_speed || plan.extra?.download_speed || 0;
    switch (value) {
      case '100-mbps': return planSpeed >= 100;
      case '1-gbps': return planSpeed >= 1000;
      case '5-gbps': return planSpeed >= 5000;
      default: return true;
    }
  },

  'speed-internet-range': createRangePredicate(p => p.extra?.internet_speed || p.extra?.download_speed || 0),

  'options-internet': (plan, values) => {
    const optionValues = Array.isArray(values) ? values : [values as string];
    const wifi = plan.box?.wifi;
    return optionValues.some(option => {
      switch (option) {
        case 'without-box': return plan.extra?.included_tv_player == false;
        case 'without-commitment': return plan.extra?.engagement_duration == '0';
        case 'wifi-5': return wifi == 'wi-fi-5';
        case 'wifi-6': return wifi == 'wi-fi-6';
        case 'wifi-6e': return wifi == 'wi-fi-6e';
        case 'wifi-7': return wifi == 'wi-fi-7';
        default: return true;
      }
    });
  },

  'network-internet': networkPredicate,

  'operator-internet': operatorPredicate,
};
